import { useState, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Input,
  Spinner,
  Body1,
  Caption1,
  Button,
  Badge,
} from '@fluentui/react-components';
import { Search24Regular, ArrowSort24Regular } from '@fluentui/react-icons';

export interface Column<T> {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
  getValue?: (item: T) => string | number;
}

/**
 * Each row's content for narrow viewports (≤600px). When provided,
 * DataTable renders a stack of cards instead of the horizontal-scroll
 * table — same source data, different layout.
 *
 * Convention used by callers:
 *   - title:      primary line (Name, App, etc.)
 *   - subtitle:   secondary metadata line(s) — kept short
 *   - leading:    icon / avatar to the left
 *   - trailing:   badge / amount / action button to the right
 */
export interface MobileRowContent {
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode; // optional 3rd line, smaller
  trailing?: React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  items: T[];
  loading?: boolean;
  searchPlaceholder?: string;
  searchFields?: (item: T) => string;
  getRowKey: (item: T) => string;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  actions?: React.ReactNode;
  filterTabs?: { key: string; label: string; count?: number }[];
  activeFilter?: string;
  onFilterChange?: (key: string) => void;
  /** Per-row card content for narrow viewports. When set, the table is
   *  hidden ≤600px and a stack of cards is rendered instead. Highly
   *  recommended for any list with >3 columns. */
  renderMobile?: (item: T) => MobileRowContent;
  /** Optional: group rows under sticky section headers. The function
   *  returns the group label for an item; items with the same label are
   *  bucketed together and shown under a sticky header. Works for both
   *  the desktop table and mobile cards. Pass undefined or null to
   *  disable. */
  groupBy?: (item: T) => string;
}

const useStyles = makeStyles({
  container: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    overflow: 'hidden',
    maxWidth: '100%',
    '@media (max-width: 600px)': {
      borderRadius: tokens.borderRadiusMedium,
      boxShadow: 'none',
      border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    flexWrap: 'wrap' as const,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '@media (max-width: 600px)': {
      padding: '10px 12px',
      gap: '8px',
    },
  },
  search: {
    minWidth: '240px',
    flex: '0 1 320px',
    '@media (max-width: 600px)': {
      minWidth: 0,
      flex: '1 1 100%',
      width: '100%',
    },
  },
  filters: {
    display: 'flex',
    gap: '4px',
    flex: 1,
    flexWrap: 'wrap' as const,
    '@media (max-width: 600px)': {
      flex: '1 1 100%',
      width: '100%',
      flexWrap: 'nowrap' as const,
      overflowX: 'auto' as const,
      WebkitOverflowScrolling: 'touch' as const,
      scrollbarWidth: 'none' as const,
      paddingBottom: '2px',
    },
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginLeft: 'auto',
    '@media (max-width: 600px)': {
      marginLeft: 0,
      flex: '1 1 100%',
      justifyContent: 'flex-end',
    },
  },
  tableOuter: {
    position: 'relative' as const,
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
  },
  scrollHint: {
    position: 'absolute' as const,
    top: 0,
    right: 0,
    bottom: 0,
    width: '20px',
    pointerEvents: 'none' as const,
    background: `linear-gradient(to right, transparent, ${tokens.colorNeutralBackground1})`,
    '@media (min-width: 900px)': {
      display: 'none',
    },
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    whiteSpace: 'nowrap' as const,
    cursor: 'default',
    userSelect: 'none' as const,
  },
  thSortable: {
    cursor: 'pointer',
    '&:hover': {
      color: tokens.colorBrandForeground1,
    },
  },
  td: {
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    fontSize: '14px',
  },
  tr: {
    transition: 'background-color 0.1s',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  trClickable: {
    cursor: 'pointer',
  },
  empty: {
    padding: '48px 24px',
    textAlign: 'center' as const,
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '48px',
  },
  count: {
    padding: '12px 20px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '@media (max-width: 600px)': {
      display: 'none',
    },
  },
  // ── Mobile card list (≤600px) ──────────────────────────────────────────
  // Hidden on wide viewports; on narrow it's the only thing shown.
  mobileList: {
    display: 'none',
    '@media (max-width: 600px)': {
      display: 'flex',
      flexDirection: 'column' as const,
    },
  },
  // Hide the desktop table on narrow viewports when renderMobile is given
  hideOnMobile: {
    '@media (max-width: 600px)': {
      display: 'none',
    },
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    borderBottom: `0.5px solid ${tokens.colorNeutralStroke3}`,
    minHeight: '64px',
    '&:last-child': { borderBottom: 'none' },
    '&:active': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  cardClickable: {
    cursor: 'pointer',
  },
  cardLeading: {
    flexShrink: 0,
  },
  cardMain: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '3px',
  },
  cardTitle: {
    fontSize: '15px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    lineHeight: 1.3,
  },
  cardSubtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    lineHeight: 1.3,
  },
  cardMeta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    marginTop: '1px',
  },
  cardTrailing: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  // ── Sticky group headers (Obsidian-style) ───────────────────────────────
  groupHeader: {
    position: 'sticky' as const,
    top: 0,
    background: tokens.colorNeutralBackground2,
    padding: '6px 16px',
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.6px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    zIndex: 1,
    fontFamily: '"SF Mono", ui-monospace, Menlo, monospace',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  groupCount: {
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    fontSize: '10px',
  },
});

export function DataTable<T>({
  columns,
  items,
  loading,
  searchPlaceholder = 'Search...',
  searchFields,
  getRowKey,
  onRowClick,
  emptyMessage = 'No items found',
  actions,
  filterTabs,
  activeFilter,
  onFilterChange,
  renderMobile,
  groupBy,
}: DataTableProps<T>) {
  const styles = useStyles();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const filteredItems = useMemo(() => {
    let result = items;

    if (search && searchFields) {
      const lower = search.toLowerCase();
      result = result.filter((item) =>
        searchFields(item).toLowerCase().includes(lower),
      );
    }

    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      if (col?.getValue) {
        const getValue = col.getValue;
        result = [...result].sort((a, b) => {
          const aVal = getValue(a);
          const bVal = getValue(b);
          const cmp =
            typeof aVal === 'number' && typeof bVal === 'number'
              ? aVal - bVal
              : String(aVal).localeCompare(String(bVal));
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }
    }

    return result;
  }, [items, search, searchFields, sortKey, sortDir, columns]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const renderMobileCard = (item: T) => {
    if (!renderMobile) return null;
    const m = renderMobile(item);
    return (
      <div
        key={getRowKey(item)}
        className={`${styles.card} ${onRowClick ? styles.cardClickable : ''}`}
        onClick={onRowClick ? () => onRowClick(item) : undefined}
      >
        {m.leading && <div className={styles.cardLeading}>{m.leading}</div>}
        <div className={styles.cardMain}>
          <div className={styles.cardTitle}>{m.title}</div>
          {m.subtitle && (
            <div className={styles.cardSubtitle}>{m.subtitle}</div>
          )}
          {m.meta && <div className={styles.cardMeta}>{m.meta}</div>}
        </div>
        {m.trailing && (
          <div className={styles.cardTrailing}>{m.trailing}</div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        {searchFields && (
          <Input
            className={styles.search}
            contentBefore={<Search24Regular />}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(_, data) => setSearch(data.value)}
            size="medium"
          />
        )}

        {filterTabs && (
          <div className={styles.filters}>
            {filterTabs.map((tab) => (
              <Button
                key={tab.key}
                appearance={activeFilter === tab.key ? 'primary' : 'subtle'}
                size="small"
                onClick={() => onFilterChange?.(tab.key)}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <Badge
                    appearance="filled"
                    color={activeFilter === tab.key ? 'brand' : 'informative'}
                    size="small"
                    style={{ marginLeft: 6 }}
                  >
                    {tab.count}
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        )}

        {actions && <div className={styles.actions}>{actions}</div>}
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spinner size="medium" label="Loading..." />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className={styles.empty}>
          <Body1>{emptyMessage}</Body1>
        </div>
      ) : (
        <>
          {/* Mobile card list — only visible ≤600px when renderMobile is provided */}
          {renderMobile && (
            <div className={styles.mobileList}>
              {(() => {
                // Group items if groupBy is provided
                if (groupBy) {
                  const groups = new Map<string, T[]>();
                  for (const item of filteredItems) {
                    const key = groupBy(item) || '—';
                    const arr = groups.get(key) ?? [];
                    arr.push(item);
                    groups.set(key, arr);
                  }
                  const sorted = Array.from(groups.entries()).sort((a, b) =>
                    a[0].localeCompare(b[0]),
                  );
                  return sorted.map(([label, list]) => (
                    <div key={label}>
                      <div className={styles.groupHeader}>
                        <span>{label}</span>
                        <span className={styles.groupCount}>{list.length}</span>
                      </div>
                      {list.map((item) => renderMobileCard(item))}
                    </div>
                  ));
                }
                // Flat list
                return filteredItems.map((item) => renderMobileCard(item));
              })()}
            </div>
          )}

          {/* Desktop table — hidden ≤600px when mobile rendering is available */}
          <div className={`${styles.tableOuter} ${renderMobile ? styles.hideOnMobile : ''}`}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`${styles.th} ${col.sortable ? styles.thSortable : ''}`}
                      style={{ width: col.width }}
                      onClick={
                        col.sortable ? () => handleSort(col.key) : undefined
                      }
                    >
                      {col.label}
                      {col.sortable && sortKey === col.key && (
                        <ArrowSort24Regular
                          style={{
                            marginLeft: 4,
                            fontSize: 14,
                            transform:
                              sortDir === 'desc'
                                ? 'rotate(180deg)'
                                : undefined,
                          }}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr
                    key={getRowKey(item)}
                    className={`${styles.tr} ${onRowClick ? styles.trClickable : ''}`}
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className={styles.td}>
                        {col.render
                          ? col.render(item)
                          : col.getValue
                            ? String(col.getValue(item))
                            : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.scrollHint} aria-hidden="true" />
          </div>

          <div className={styles.count}>
            <Caption1>
              {filteredItems.length} of {items.length} items
            </Caption1>
          </div>
        </>
      )}
    </div>
  );
}
